// PopupOption.jsx

import React, { useState } from 'react';

// Icons
import Icon from 'svg/Icon';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './PopupOption.css';

/**
 * PopupOption
 * 
 * Renders an individual option within a popup, with optional icon and settings button.
 * 
 * @param {React.Component} icon - Optional icon component to display.
 * @param {string} iconKey - Optional icon key to render via Icon.
 * @param {string} label - Label for the option.
 * @param {boolean} selected - Whether the option is currently selected.
 * @param {string} labelColor - Optional label color override.
 * @param {function} onClick - Handler for when the option is clicked.
 * @param {ReactNode} rightContent - Optional right-side content to display.
 * @param {React.Component} settingsIcon - Optional settings icon component to display.
 * @param {string} settingsIconKey - Optional settings icon key to render via Icon.
 * @param {function} onSettingsClick - Handler for when the settings icon is clicked.
 *  
 * @returns {ReactNode} - PopupOption.
 */
const PopupOption = React.memo(({ icon: IconComponent, iconKey, label, note = '', selected = false, disabled = false, labelColor, onClick, rightContent, settingsIcon: SettingsIconComponent, settingsIconKey, onSettingsClick }) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // Hover State
    const [hovered, setHovered] = useState(false);
    const [editHovered, setEditHovered] = useState(false);

    // ----- RENDER ----- //

    return (

        <div
            className={`popup-option-container${disabled ? ' disabled' : ''}`}
            style={{
                color: hovered ? theme.buttonHoverLabelColor : disabled ? theme.foregroundMuted : theme.foreground,
                backgroundColor: !disabled && hovered ? theme.buttonHoverBackgroundColor : 'transparent',
            }}
            onClick={disabled ? undefined : onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => {
                setHovered(false);
                setEditHovered(false);
            }}
        >
            {(iconKey || IconComponent) && (
                <div className="popup-option-icon">
                    {iconKey ? (
                        <Icon
                            iconKey={iconKey}
                            width={18}
                            height={18}
                            color={hovered ? theme.buttonHoverIconColor : selected ? theme.foreground : theme.foregroundMuted}
                        />
                    ) : (
                        <IconComponent
                            width={18}
                            height={18}
                            color={(selected || hovered) ? theme.buttonHoverForeground : theme.foregroundMuted}
                        />
                    )}
                </div>
            )}
            <div className="popup-option-text">
                <span
                    className="popup-option-label"
                    style={labelColor ? { color: labelColor } : undefined}
                >
                    {label}
                </span>
                {note && (
                    <span
                        className="popup-option-note"
                        style={{ color: hovered ? theme.buttonHoverForeground : theme.foregroundMuted }}
                    >
                        {note}
                    </span>
                )}
            </div>
            {rightContent && (
                <div className="popup-option-right">
                    {rightContent}
                </div>
            )}
            {((settingsIconKey || SettingsIconComponent) && onSettingsClick) &&
                <div
                    className="popup-option-edit"
                    style={{
                        backgroundColor: editHovered || hovered ? theme.backgroundHover : 'transparent',
                    }}
                    onMouseEnter={() => setEditHovered(true)}
                    onMouseLeave={() => setEditHovered(false)}
                    onClick={onSettingsClick}
                >
                    {settingsIconKey ? (
                        <Icon
                            iconKey={settingsIconKey}
                            color={hovered ? theme.buttonHoverLabelColor : theme.foregroundMuted}
                            width={18}
                            height={18}
                        />
                    ) : (
                        <SettingsIconComponent
                            color={hovered ? theme.buttonHoverLabelColor : theme.foregroundMuted}
                            width={18}
                            height={18}
                        />
                    )}
                </div>
            }
        </div>
    );

});

// Dev Tools
PopupOption.displayName = 'PopupOption';

export default PopupOption;

